import React, { useState } from "react";
import { Box, IconButton, Tooltip, alpha, useTheme } from "@mui/material";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";

/** Themed code sample with copy-to-clipboard — used throughout the API docs. */
const CodeBlock: React.FC<{ children: string }> = ({ children }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Box sx={{ position: "relative", "&:hover .code-copy": { opacity: 1 } }}>
      <Tooltip title={copied ? "Copied" : "Copy"}>
        <IconButton
          className="code-copy"
          aria-label="Copy code sample"
          size="small"
          onClick={copy}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            opacity: 0,
            transition: "opacity 160ms ease",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            "&:focus-visible": { opacity: 1 },
          }}
        >
          {copied ? (
            <CheckRounded sx={{ fontSize: 15, color: "success.main" }} />
          ) : (
            <ContentCopyRounded sx={{ fontSize: 15 }} />
          )}
        </IconButton>
      </Tooltip>

      <Box
        component="pre"
        sx={{
          margin: 0,
          padding: "14px 16px",
          borderRadius: "12px",
          overflowX: "auto",
          fontSize: 12.5,
          lineHeight: 1.6,
          fontFamily:
            "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha("#FFFFFF", 0.04)
              : alpha("#172033", 0.03),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default CodeBlock;
